/**
 * AIAssistantPanel — Panel unificado de asistencia IA.
 *
 * Consolida 3 superficies previas (AIAnalysisPanel + FindingsMatchPanel +
 * AITutorUI) en una sola con 3 tabs. Los 3 tabs comparten contexto: siempre
 * se construye a partir de `state.revealedFindings`, nunca del grafo completo.
 * Esto evita que la IA "spoilee" labs/imágenes que el alumno aún no pidió.
 *
 * Tabs:
 *   - Diagnóstico (Qwen) — DDx, tests, triage, factores de riesgo
 *   - Patrones (pgvector) — patrones clínicos semánticamente similares
 *   - Tutor (Qwen socrático) — chat que guía con preguntas
 *
 * Gates pedagógicos: Diagnóstico y Patrones requieren ≥3 hallazgos revelados.
 * Tutor no tiene gate: puede guiar desde el primer momento.
 *
 * UX: cierra con ESC / click fuera. Recuerda el último tab. Feedback 👍/👎
 * re-aparece con cada sugerencia nueva. Tutor tiene auto-scroll, mantiene
 * focus en el input y muestra "Escribiendo…" mientras espera.
 */

import state from '../core/state';
import { Patient } from '../medical/Patient';
import { authService } from '../auth/AuthService';
import { qwenClient, type AIAnalysisResult } from './AlibabaQwenClient';
import { matchFindings, type PatternMatch } from './FindingMatcher';
import type { PatientGraph } from '../types/medical';

type Tab = 'diagnostico' | 'patrones' | 'tutor';

const PANEL_ID = 'cgt-ai-assistant';
const STYLE_ID = 'cgt-ai-assistant-styles';
const LAST_TAB_KEY = 'cgt_ai_last_tab';
const MIN_FINDINGS_FOR_AI = 3;

const TRIAGE_COLORS: Record<string, string> = {
  immediate: '#FF8A80',
  delayed: '#FFC671',
  minor: '#69E894',
  expectant: '#8E8E93',
  deceased: '#3A3A3C',
};

interface TutorTurn {
  role: 'user' | 'assistant';
  text: string;
}

export class AIAssistantPanel {
  private root: HTMLElement | null = null;
  private activeTab: Tab = 'patrones';
  private patient: Patient | null = null;

  private lastAnalysis: AIAnalysisResult | null = null;
  private lastPatterns: PatternMatch[] | null = null;
  private tutorHistory: TutorTurn[] = [];

  private patternsRefreshTimer: number | null = null;
  private findingUnsub: (() => void) | null = null;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;

  /**
   * Callback opcional que main.ts usa para coordinar el sidebar (colapsarlo
   * mientras el panel está abierto, restaurarlo al cerrar). Se limpia en
   * cada close() para evitar ejecuciones múltiples.
   */
  onClose?: () => void;

  setPatient(patient: Patient | null): void {
    this.patient = patient;
    this.lastAnalysis = null;
    this.lastPatterns = null;
    this.tutorHistory = [];
  }

  open(tab?: Tab): void {
    if (!this.patient) return;
    this.injectStyles();
    this.activeTab = tab ?? this.restoreLastTab();
    // Rubric: si el alumno abre el tab Diagnóstico antes de proponer su dx,
    // lo penaliza al scorear (premia el razonamiento propio sobre la consulta
    // directa a la IA). Patrones y Tutor no penalizan: son guía formativa.
    if (this.activeTab === 'diagnostico' && !state.userProposedDx) {
      state.userOpenedAIBeforeDx = true;
    }
    if (!this.root) {
      this.root = this.buildShell();
      document.body.appendChild(this.root);
      requestAnimationFrame(() => this.root?.classList.add('cgt-ai-visible'));
      this.findingUnsub = state.onEvent('FINDING_REVEALED', () => this.onFindingRevealed());
      this.keyHandler = (e) => { if (e.key === 'Escape') this.close(); };
      document.addEventListener('keydown', this.keyHandler);
    }
    this.renderTab();
  }

  close(): void {
    this.findingUnsub?.();
    this.findingUnsub = null;
    if (this.keyHandler) {
      document.removeEventListener('keydown', this.keyHandler);
      this.keyHandler = null;
    }
    if (this.patternsRefreshTimer !== null) {
      window.clearTimeout(this.patternsRefreshTimer);
      this.patternsRefreshTimer = null;
    }
    if (!this.root) return;
    const el = this.root;
    el.classList.remove('cgt-ai-visible');
    setTimeout(() => el.remove(), 200);
    this.root = null;
    const cb = this.onClose;
    this.onClose = undefined;
    cb?.();
  }

  /**
   * Reacción al FINDING_REVEALED: si el tab actual está bajo gate,
   * re-render inmediato para reflejar el nuevo contador. Si ya pasó el
   * gate, se programa recarga con throttle (para no spamear a Qwen).
   */
  private onFindingRevealed(): void {
    const underGate = this.countRelevantRevealed() < MIN_FINDINGS_FOR_AI;
    if (underGate && (this.activeTab === 'diagnostico' || this.activeTab === 'patrones')) {
      this.renderTab();
      return;
    }
    this.scheduleReload();
  }

  private restoreLastTab(): Tab {
    const saved = localStorage.getItem(LAST_TAB_KEY);
    if (saved === 'diagnostico' || saved === 'patrones' || saved === 'tutor') return saved;
    return 'patrones';
  }

  private scheduleReload(): void {
    if (this.patternsRefreshTimer !== null) return;
    this.patternsRefreshTimer = window.setTimeout(() => {
      this.patternsRefreshTimer = null;
      this.lastPatterns = null;
      this.lastAnalysis = null;
      if (this.activeTab === 'patrones' || this.activeTab === 'diagnostico') {
        this.renderTab();
      }
    }, 3000);
  }

  private buildShell(): HTMLElement {
    const root = document.createElement('div');
    root.id = PANEL_ID;
    root.innerHTML = `
      <header>
        <div class="cgt-ai-title">
          <span class="cgt-ai-logo">🧠</span>
          <div>
            <h2>Asistente IA</h2>
            <p>Razonamiento clínico asistido por Qwen + pgvector</p>
          </div>
        </div>
        <button type="button" class="cgt-ai-close" data-close aria-label="Cerrar (ESC)" title="Cerrar (ESC)">×</button>
      </header>
      <nav class="cgt-ai-tabs">
        <button type="button" data-tab="diagnostico">Diagnóstico</button>
        <button type="button" data-tab="patrones">Patrones</button>
        <button type="button" data-tab="tutor">Tutor</button>
      </nav>
      <div class="cgt-ai-body"></div>
    `;
    root.querySelector('[data-close]')?.addEventListener('click', () => this.close());
    root.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach(btn => {
      btn.addEventListener('click', () => {
        const t = btn.dataset.tab as Tab;
        this.activeTab = t;
        localStorage.setItem(LAST_TAB_KEY, t);
        this.renderTab();
      });
    });
    return root;
  }

  private async sendFeedback(kind: 'up' | 'down', zone: HTMLElement): Promise<void> {
    if (!authService.isAuthenticated) return;
    zone.innerHTML = `<span class="cgt-ai-thanks">Gracias por tu feedback ${kind === 'up' ? '🙏' : '✏️'}.</span>`;
    try {
      await authService.request('/ai/feedback', {
        method: 'POST',
        body: JSON.stringify({
          tab: this.activeTab,
          helpful: kind === 'up',
          case_id: this.patient?.data.id ?? 'unknown',
        }),
      });
    } catch {
      // backend puede no tener el endpoint: fail silencioso
    }
  }

  private feedbackBlock(): string {
    return `
      <div class="cgt-ai-fb-zone">
        <span>¿Te sirvió esta sugerencia?</span>
        <button type="button" data-fb="up">👍</button>
        <button type="button" data-fb="down">👎</button>
      </div>
    `;
  }

  private attachFeedbackHandlers(body: HTMLElement): void {
    const zone = body.querySelector<HTMLElement>('.cgt-ai-fb-zone');
    if (!zone) return;
    zone.querySelectorAll<HTMLButtonElement>('[data-fb]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.sendFeedback(btn.dataset.fb as 'up' | 'down', zone);
      });
    });
  }

  private attachGateHandlers(body: HTMLElement): void {
    body.querySelector<HTMLButtonElement>('[data-go-tutor]')?.addEventListener('click', () => {
      this.activeTab = 'tutor';
      localStorage.setItem(LAST_TAB_KEY, 'tutor');
      this.renderTab();
    });
  }

  private renderTab(): void {
    const body = this.root?.querySelector<HTMLElement>('.cgt-ai-body');
    if (!body) return;
    this.root?.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach(b => {
      b.classList.toggle('cgt-ai-tab-active', b.dataset.tab === this.activeTab);
    });
    if (this.activeTab === 'diagnostico') this.renderDiagnostico(body);
    else if (this.activeTab === 'patrones') this.renderPatrones(body);
    else this.renderTutor(body);
  }

  // ─────────────────────────────────────────────────────────────
  // Tab: Diagnóstico diferencial (Qwen Max)
  // ─────────────────────────────────────────────────────────────
  private async renderDiagnostico(body: HTMLElement): Promise<void> {
    const revealedCount = this.countRelevantRevealed();
    if (revealedCount < MIN_FINDINGS_FOR_AI) {
      body.innerHTML = this.renderInsufficientFindings(revealedCount);
      this.attachGateHandlers(body);
      return;
    }
    // Gate pedagógico adicional: la IA no da la respuesta hasta que el
    // alumno haya intentado diagnosticar primero en el DifferentialPanel.
    // Evita el anti-patrón "abro el caso → pido DDx → copio la respuesta".
    if (!state.userProposedDx) {
      body.innerHTML = this.renderNeedsProposal();
      this.attachProposalHandlers(body);
      return;
    }
    if (this.lastAnalysis) {
      body.innerHTML = this.renderDiagnosticoContent(this.lastAnalysis);
      this.attachFeedbackHandlers(body);
      return;
    }
    body.innerHTML = '<div class="cgt-ai-loading">Analizando caso con Qwen…</div>';
    if (!this.patient) return;
    try {
      // Construimos un PatientGraph filtrado: sólo los hallazgos ya revelados
      // al alumno. Evita que la IA use labs/imágenes que aún no pidió.
      const filtered = this.buildRevealedPatient();
      const res = await qwenClient.analyzeMedicalCase(filtered);
      if (!res) {
        body.innerHTML = '<div class="cgt-ai-empty">No hay análisis disponible. Intenta de nuevo.</div>';
        return;
      }
      this.lastAnalysis = res;
      body.innerHTML = this.renderDiagnosticoContent(res);
      this.attachFeedbackHandlers(body);
    } catch (e) {
      body.innerHTML = `<div class="cgt-ai-error">Error: ${this.escape(String(e))}</div>`;
    }
  }

  private renderDiagnosticoContent(r: AIAnalysisResult): string {
    const color = TRIAGE_COLORS[r.triageRecommendation] ?? '#8e8e93';
    const revealedCount = this.countRelevantRevealed();
    return `
      ${r.isMock ? '<div class="cgt-ai-hint">🧪 Análisis local (preview, sin API).</div>' : ''}
      <div class="cgt-ai-meta">Basado en <strong>${revealedCount}</strong> hallazgos que vos exploraste.</div>
      <div class="cgt-ai-score">
        <div>
          <span class="cgt-ai-lbl">Confianza</span>
          <strong>${r.confidence}%</strong>
        </div>
        <div>
          <span class="cgt-ai-lbl">Triage</span>
          <strong style="color:${color}">${r.triageRecommendation}</strong>
        </div>
      </div>

      <h4>Diagnósticos diferenciales</h4>
      <ul class="cgt-ai-dx">
        ${r.differentialDiagnoses.slice(0, 5).map(d => `
          <li>
            <div class="cgt-ai-dx-head">
              <strong>${this.escape(d.condition)}</strong>
              <span class="cgt-ai-prob">${d.probability}%</span>
            </div>
            <div class="cgt-ai-dx-bar"><div style="width:${d.probability}%;background:${this.probColor(d.probability)}"></div></div>
            <p>${this.escape(d.reasoning)}</p>
          </li>
        `).join('')}
      </ul>

      ${r.recommendedTests.length ? `
        <h4>Exámenes recomendados</h4>
        <ul class="cgt-ai-list">
          ${r.recommendedTests.slice(0, 6).map(t => `<li>🧪 ${this.escape(t)}</li>`).join('')}
        </ul>
      ` : ''}

      ${r.riskFactors.length ? `
        <h4>Factores de riesgo</h4>
        <ul class="cgt-ai-list">
          ${r.riskFactors.slice(0, 5).map(f => `<li>⚠️ ${this.escape(f)}</li>`).join('')}
        </ul>
      ` : ''}

      ${this.feedbackBlock()}
    `;
  }

  // ─────────────────────────────────────────────────────────────
  // Tab: Patrones candidatos (pgvector)
  // ─────────────────────────────────────────────────────────────
  private async renderPatrones(body: HTMLElement): Promise<void> {
    const revealedCount = this.countRelevantRevealed();
    if (revealedCount < MIN_FINDINGS_FOR_AI) {
      body.innerHTML = this.renderInsufficientFindings(revealedCount);
      this.attachGateHandlers(body);
      return;
    }
    if (this.lastPatterns) {
      body.innerHTML = this.renderPatronesContent(this.lastPatterns);
      this.attachFeedbackHandlers(body);
      return;
    }
    body.innerHTML = '<div class="cgt-ai-loading">Buscando patrones semánticos…</div>';
    const text = this.buildFindingsText();
    try {
      const patterns = await matchFindings(text, 5);
      this.lastPatterns = patterns;
      body.innerHTML = this.renderPatronesContent(patterns);
      this.attachFeedbackHandlers(body);
    } catch (e) {
      body.innerHTML = `<div class="cgt-ai-error">Error: ${this.escape(String(e))}</div>`;
    }
  }

  private renderPatronesContent(patterns: PatternMatch[]): string {
    if (patterns.length === 0) {
      return '<div class="cgt-ai-empty">Sin coincidencias. Probá revelando más hallazgos.</div>';
    }
    const detectedIds = new Set(state.detectedPatterns.map(p => p.pattern.id));
    return `
      <p class="cgt-ai-hint">Patrones clínicos compatibles con los hallazgos revelados. El ✓ indica que los criterios exactos ya se cumplen.</p>
      <ul class="cgt-ai-patterns">
        ${patterns.map(p => {
          const color = TRIAGE_COLORS[p.urgency] ?? '#8e8e93';
          const pct = Math.round(p.similarity * 100);
          const exact = detectedIds.has(p.pattern_id);
          return `
            <li>
              <div class="cgt-ai-p-head">
                <span class="cgt-ai-dot" style="background:${color}"></span>
                <strong>${this.escape(p.pattern_name)}</strong>
                ${exact ? '<span class="cgt-ai-check" title="Criterios exactos cumplidos">✓</span>' : ''}
                <span class="cgt-ai-prob">${pct}%</span>
              </div>
              <div class="cgt-ai-p-cond">${this.escape(p.condition)}</div>
              <div class="cgt-ai-dx-bar"><div style="width:${pct}%;background:${color}"></div></div>
            </li>
          `;
        }).join('')}
      </ul>
      ${this.feedbackBlock()}
    `;
  }

  // ─────────────────────────────────────────────────────────────
  // Tab: Tutor socrático
  // Render mutable: mantiene el input montado para no perder focus
  // y hace auto-scroll al último mensaje.
  // ─────────────────────────────────────────────────────────────
  private renderTutor(body: HTMLElement): void {
    body.innerHTML = `
      <div class="cgt-ai-tutor-head">
        <p class="cgt-ai-hint" style="margin:0;flex:1;">El tutor no te da la respuesta. Te guía con preguntas.</p>
        <button type="button" class="cgt-ai-tutor-clear" data-clear title="Nuevo chat" aria-label="Nuevo chat">↺</button>
      </div>
      <div class="cgt-ai-chat" data-chat></div>
      <form class="cgt-ai-input" data-ask>
        <input type="text" name="q" placeholder="Escribí tu pregunta…" autocomplete="off" />
        <button type="submit" aria-label="Enviar">→</button>
      </form>
    `;
    const chat = body.querySelector<HTMLElement>('[data-chat]')!;
    this.repaintTutorChat(chat);
    const form = body.querySelector<HTMLFormElement>('[data-ask]')!;
    const input = form.querySelector<HTMLInputElement>('[name="q"]')!;
    input.focus();

    body.querySelector<HTMLButtonElement>('[data-clear]')?.addEventListener('click', () => {
      this.tutorHistory = [];
      this.repaintTutorChat(chat);
      input.focus();
    });

    const submitQuestion = async (question: string): Promise<void> => {
      if (!question.trim()) return;
      input.value = '';
      this.tutorHistory.push({ role: 'user', text: question });
      this.appendTutorBubble(chat, 'user', question);

      // Bubble "Escribiendo…" temporal
      const typing = this.appendTutorBubble(chat, 'assistant', '···', 'cgt-ai-typing');

      const ctx = this.buildTutorContext();
      const answer = await qwenClient.askTutor(question, ctx);
      typing.remove();
      const text = answer || 'No pude responder ahora. Probá reformular la pregunta.';
      this.tutorHistory.push({ role: 'assistant', text });
      this.appendTutorBubble(chat, 'assistant', text);
      input.focus();
    };

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      await submitQuestion(input.value.trim());
    });

    // Chips de preguntas sugeridas — sólo disparan cuando el chat está vacío.
    chat.addEventListener('click', async (e) => {
      const t = e.target as HTMLElement;
      const chip = t.closest<HTMLButtonElement>('[data-suggestion]');
      if (!chip) return;
      const q = chip.dataset.suggestion ?? '';
      await submitQuestion(q);
    });
  }

  private static readonly TUTOR_SUGGESTIONS = [
    '¿Qué descartarías primero?',
    '¿Qué examen pediría ahora?',
    '¿Qué signos de alarma no debería pasar por alto?',
    'Explicame la fisiopatología en 3 frases.',
  ];

  private repaintTutorChat(chat: HTMLElement): void {
    chat.innerHTML = '';
    if (this.tutorHistory.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'cgt-ai-empty';
      empty.innerHTML = `
        <p style="margin:0 0 10px;">Sin mensajes. Probá con una de estas preguntas:</p>
        <div class="cgt-ai-suggestions">
          ${AIAssistantPanel.TUTOR_SUGGESTIONS.map(q => `
            <button type="button" data-suggestion="${this.escape(q)}">${this.escape(q)}</button>
          `).join('')}
        </div>
      `;
      chat.appendChild(empty);
      return;
    }
    this.tutorHistory.forEach(t => this.appendTutorBubble(chat, t.role, t.text));
  }

  private appendTutorBubble(
    chat: HTMLElement,
    role: 'user' | 'assistant',
    text: string,
    extraClass = ''
  ): HTMLElement {
    // Si es el primer mensaje, limpia el empty state
    const empty = chat.querySelector('.cgt-ai-empty');
    if (empty) empty.remove();
    const bubble = document.createElement('div');
    bubble.className = `cgt-ai-turn ${role}${extraClass ? ` ${extraClass}` : ''}`;
    bubble.textContent = text;
    chat.appendChild(bubble);
    // Auto-scroll al bottom
    chat.scrollTop = chat.scrollHeight;
    return bubble;
  }

  // ─────────────────────────────────────────────────────────────
  // Helpers: construcción de contexto filtrado por revealedFindings
  // ─────────────────────────────────────────────────────────────

  /**
   * Cuenta hallazgos que el alumno reveló POR ACCIÓN (click / examen /
   * gesto / timeline), excluyendo los iniciales y el nodo diagnosis.
   * Gate pedagógico: fuerza a interactuar con el grafo antes de pedir IA.
   */
  private countRelevantRevealed(): number {
    if (!this.patient) return 0;
    const acted = state.revealedByAction;
    let n = 0;
    this.patient.nodes.forEach(node => {
      if (!acted.has(node.data.id)) return;
      if (node.data.type === 'diagnosis') return;
      n++;
    });
    return n;
  }

  /**
   * Construye un PatientGraph cuyo `nodes` es sólo los revelados (excluyendo
   * el nodo diagnosis). La IA de Diagnóstico consume este objeto en lugar del
   * grafo completo para no spoilear exámenes pendientes. Además ocultamos
   * el triagePriority oficial para que Qwen no lo use como atajo.
   */
  private buildRevealedPatient(): PatientGraph {
    const source = this.patient!.data;
    const revealed = state.revealedFindings;
    const filteredNodes = source.nodes.filter(
      n => revealed.has(n.id) && n.type !== 'diagnosis'
    );
    return {
      ...source,
      nodes: filteredNodes,
      // El triage oficial del caso es la respuesta correcta; si se la pasamos
      // a Qwen junto con el pedido "clasificá el triage" colapsa el ejercicio.
      // 'minor' es neutro y explícitamente no revela el ground truth.
      triagePriority: 'minor',
    };
  }

  private buildFindingsText(): string {
    if (!this.patient) return '';
    const revealed = state.revealedFindings;
    const parts: string[] = [];
    const demo = this.patient.data.demographics;
    parts.push(`Paciente ${demo.age}a ${demo.sex}.`);
    if (this.patient.data.chiefComplaint) parts.push(this.patient.data.chiefComplaint);

    const findings: string[] = [];
    this.patient.nodes.forEach(n => {
      if (!revealed.has(n.data.id)) return;
      if (n.data.type === 'diagnosis') return;
      const d = n.data;
      const label = d.concept.display;
      let val = '';
      if (d.valueQuantity) val = ` ${d.valueQuantity.value} ${d.valueQuantity.unit}`;
      else if (typeof d.valueString === 'string') val = ` ${d.valueString}`;
      else if (typeof d.valueBoolean === 'boolean') val = d.valueBoolean ? ' (presente)' : ' (ausente)';
      const sev = d.severity ? ` [${d.severity}]` : '';
      findings.push(`${label}${val}${sev}`);
    });
    if (findings.length) parts.push(`Hallazgos: ${findings.join('; ')}.`);
    return parts.join(' ');
  }

  private buildTutorContext(): string {
    const findings = this.buildFindingsText();
    const hypothesis = state.currentHypothesis;
    const hypPart = hypothesis
      ? ` Hipótesis actual del alumno: ${hypothesis.dxName} (${hypothesis.probability}%).`
      : '';
    return `${findings}${hypPart}`;
  }

  /**
   * Gate pedagógico que pide al alumno proponer un dx antes de ver el
   * análisis de la IA. No bloquea Patrones ni Tutor (que son formativos
   * sin dar respuesta directa).
   */
  private renderNeedsProposal(): string {
    return `
      <div class="cgt-ai-gate">
        <div class="cgt-ai-gate-icon">🧠</div>
        <h4>Proponé tu diagnóstico primero</h4>
        <p>La IA no está para darte la respuesta. Está para validar tu razonamiento.</p>
        <p class="cgt-ai-gate-hint">Abrí el <strong>Panel diferencial</strong>, elegí tu dx principal (correcto o no) y después volvé acá. Podés seguir usando <strong>Patrones</strong> y <strong>Tutor</strong> mientras tanto.</p>
        <button type="button" class="cgt-ai-gate-btn" data-open-differential>Abrir Panel diferencial</button>
      </div>
    `;
  }

  private attachProposalHandlers(body: HTMLElement): void {
    body.querySelector<HTMLButtonElement>('[data-open-differential]')?.addEventListener('click', () => {
      // Cerrar el panel IA y abrir el diferencial vía app
      this.close();
      const app = (window as unknown as { app?: { startDifferential?: () => void } }).app;
      app?.startDifferential?.();
    });
  }

  private renderInsufficientFindings(count: number): string {
    const missing = MIN_FINDINGS_FOR_AI - count;
    return `
      <div class="cgt-ai-gate">
        <div class="cgt-ai-gate-icon">🔒</div>
        <h4>Explorá al menos ${MIN_FINDINGS_FOR_AI} hallazgos</h4>
        <p>Llevás <strong>${count}</strong> ${count === 1 ? 'hallazgo explorado' : 'hallazgos explorados'} por acción. Faltan <strong>${missing}</strong> para pedir análisis.</p>
        <p class="cgt-ai-gate-hint">Clickeá nodos del grafo o ordená exámenes. Los iniciales del caso no cuentan para evitar atajos. Mientras tanto, el <strong>Tutor</strong> puede guiarte sin datos previos.</p>
        <button type="button" class="cgt-ai-gate-btn" data-go-tutor>Ir al Tutor</button>
      </div>
    `;
  }

  private probColor(p: number): string {
    if (p >= 70) return '#FF453A';
    if (p >= 40) return '#FF9F0A';
    return '#8E8E93';
  }

  private escape(text: string): string {
    return text.replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c] ?? c));
  }

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      #${PANEL_ID} {
        position: fixed; bottom: 24px; right: 24px;
        width: 380px; max-height: 78vh;
        background: rgba(10,10,10,0.94); backdrop-filter: blur(16px);
        border: 0.5px solid rgba(255,255,255,0.12); border-radius: 14px;
        color: #f5f5f7; font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
        display: flex; flex-direction: column; overflow: hidden;
        box-shadow: 0 20px 60px rgba(0,0,0,0.5);
        opacity: 0; transform: translateY(12px);
        transition: opacity 0.25s, transform 0.25s;
        z-index: 9100;
      }
      #${PANEL_ID}.cgt-ai-visible { opacity: 1; transform: translateY(0); }
      #${PANEL_ID} header {
        display: flex; align-items: start; justify-content: space-between;
        padding: 14px 16px 10px;
      }
      #${PANEL_ID} .cgt-ai-title { display: flex; gap: 10px; align-items: center; }
      #${PANEL_ID} .cgt-ai-logo {
        display: inline-flex; width: 32px; height: 32px; align-items: center; justify-content: center;
        font-size: 18px; border-radius: 9px;
        background: linear-gradient(135deg, rgba(41,151,255,0.2), rgba(191,90,242,0.2));
        border: 0.5px solid rgba(41,151,255,0.3);
      }
      #${PANEL_ID} h2 { font-size: 14px; font-weight: 700; }
      #${PANEL_ID} header p { font-size: 10px; color: #6e6e73; margin-top: 2px; }
      #${PANEL_ID} .cgt-ai-close {
        width: 24px; height: 24px; border-radius: 50%;
        background: rgba(255,255,255,0.06); border: none; cursor: pointer;
        color: #a1a1a6; font-size: 15px; line-height: 1;
      }
      #${PANEL_ID} .cgt-ai-tabs {
        display: flex; gap: 2px; padding: 0 14px;
        border-bottom: 0.5px solid rgba(255,255,255,0.06);
      }
      #${PANEL_ID} .cgt-ai-tabs button {
        background: transparent; border: none; color: #8e8e93;
        font-size: 12px; font-weight: 500; padding: 8px 12px;
        border-bottom: 2px solid transparent; cursor: pointer; font-family: inherit;
      }
      #${PANEL_ID} .cgt-ai-tabs button:hover { color: #f5f5f7; }
      #${PANEL_ID} .cgt-ai-tab-active { color: #2997FF !important; border-bottom-color: #2997FF !important; }
      #${PANEL_ID} .cgt-ai-body { padding: 14px 16px; overflow-y: auto; flex: 1; font-size: 12px; }
      #${PANEL_ID} .cgt-ai-loading, #${PANEL_ID} .cgt-ai-empty { padding: 30px 14px; text-align: center; color: #8e8e93; font-size: 12px; line-height: 1.5; }
      #${PANEL_ID} .cgt-ai-error { padding: 14px; color: #FF6961; font-size: 11px; }
      #${PANEL_ID} .cgt-ai-hint {
        padding: 10px 12px; margin-bottom: 10px;
        background: rgba(41,151,255,0.08); border-left: 2px solid #2997FF;
        border-radius: 6px; color: #a1a1a6; font-size: 11px; line-height: 1.45;
      }
      #${PANEL_ID} .cgt-ai-meta { font-size: 10.5px; color: #6e6e73; margin: 0 0 8px; }
      #${PANEL_ID} .cgt-ai-meta strong { color: #a1a1a6; font-weight: 600; }
      #${PANEL_ID} h4 {
        font-size: 10px; text-transform: uppercase; letter-spacing: 1px;
        color: #6e6e73; margin: 14px 0 8px; font-weight: 600;
      }
      #${PANEL_ID} .cgt-ai-score {
        display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 4px;
      }
      #${PANEL_ID} .cgt-ai-score > div {
        background: rgba(255,255,255,0.03); border-radius: 8px; padding: 10px;
      }
      #${PANEL_ID} .cgt-ai-lbl {
        display: block; font-size: 9px; color: #6e6e73;
        text-transform: uppercase; letter-spacing: 0.5px;
      }
      #${PANEL_ID} .cgt-ai-score strong { font-size: 14px; font-weight: 700; }
      #${PANEL_ID} .cgt-ai-dx, #${PANEL_ID} .cgt-ai-patterns { list-style: none; padding: 0; margin: 0; }
      #${PANEL_ID} .cgt-ai-dx li, #${PANEL_ID} .cgt-ai-patterns li {
        padding: 8px 0; border-bottom: 0.5px solid rgba(255,255,255,0.05);
      }
      #${PANEL_ID} .cgt-ai-dx-head, #${PANEL_ID} .cgt-ai-p-head {
        display: flex; align-items: center; gap: 8px; margin-bottom: 4px;
      }
      #${PANEL_ID} .cgt-ai-p-head strong { flex: 1; }
      #${PANEL_ID} .cgt-ai-prob {
        font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #a1a1a6;
      }
      #${PANEL_ID} .cgt-ai-dot { width: 8px; height: 8px; border-radius: 50%; }
      #${PANEL_ID} .cgt-ai-check {
        font-size: 11px; color: #30D158; font-weight: 700;
      }
      #${PANEL_ID} .cgt-ai-dx-bar {
        height: 3px; background: rgba(255,255,255,0.05);
        border-radius: 2px; overflow: hidden; margin-top: 4px;
      }
      #${PANEL_ID} .cgt-ai-dx-bar div { height: 100%; transition: width 0.3s; }
      #${PANEL_ID} .cgt-ai-dx p {
        font-size: 11px; color: #a1a1a6; margin-top: 4px; line-height: 1.5;
      }
      #${PANEL_ID} .cgt-ai-p-cond { font-size: 11px; color: #a1a1a6; margin: 2px 0 4px; }
      #${PANEL_ID} .cgt-ai-list { list-style: none; padding: 0; margin: 0; }
      #${PANEL_ID} .cgt-ai-list li { padding: 4px 0; font-size: 11px; color: #d1d1d6; }

      #${PANEL_ID} .cgt-ai-tutor-head {
        display: flex; align-items: center; gap: 8px; margin-bottom: 10px;
      }
      #${PANEL_ID} .cgt-ai-tutor-clear {
        width: 26px; height: 26px; border-radius: 50%;
        background: rgba(255,255,255,0.04); border: 0.5px solid rgba(255,255,255,0.08);
        color: #a1a1a6; font-size: 14px; cursor: pointer; line-height: 1;
        flex-shrink: 0;
      }
      #${PANEL_ID} .cgt-ai-tutor-clear:hover { color: #fff; background: rgba(255,255,255,0.1); }
      #${PANEL_ID} .cgt-ai-suggestions {
        display: flex; flex-direction: column; gap: 6px; margin-top: 4px;
      }
      #${PANEL_ID} .cgt-ai-suggestions button {
        padding: 8px 12px; background: rgba(41,151,255,0.08);
        border: 0.5px solid rgba(41,151,255,0.2); border-radius: 8px;
        color: #2997FF; cursor: pointer; font-size: 11.5px; text-align: left;
        font-family: inherit; line-height: 1.4;
      }
      #${PANEL_ID} .cgt-ai-suggestions button:hover {
        background: rgba(41,151,255,0.15); border-color: rgba(41,151,255,0.35);
      }
      #${PANEL_ID} .cgt-ai-chat {
        display: flex; flex-direction: column; gap: 8px; margin-bottom: 12px;
        max-height: 340px; overflow-y: auto; scroll-behavior: smooth;
      }
      #${PANEL_ID} .cgt-ai-turn {
        padding: 8px 12px; border-radius: 10px; font-size: 12px; line-height: 1.5;
        max-width: 90%; white-space: pre-wrap;
      }
      #${PANEL_ID} .cgt-ai-turn.user {
        align-self: flex-end;
        background: rgba(41,151,255,0.2); color: #f5f5f7;
      }
      #${PANEL_ID} .cgt-ai-turn.assistant {
        align-self: flex-start;
        background: rgba(255,255,255,0.04); color: #d1d1d6;
      }
      #${PANEL_ID} .cgt-ai-typing {
        opacity: 0.7;
        animation: cgt-ai-pulse 1s ease-in-out infinite;
        letter-spacing: 2px;
      }
      @keyframes cgt-ai-pulse {
        0%, 100% { opacity: 0.35; }
        50% { opacity: 0.9; }
      }
      #${PANEL_ID} .cgt-ai-input {
        display: flex; gap: 6px; padding-top: 6px;
        border-top: 0.5px solid rgba(255,255,255,0.06);
      }
      #${PANEL_ID} .cgt-ai-input input {
        flex: 1; padding: 8px 12px; font-size: 12px; color: #f5f5f7;
        background: rgba(255,255,255,0.04);
        border: 0.5px solid rgba(255,255,255,0.08); border-radius: 8px;
        font-family: inherit; outline: none;
      }
      #${PANEL_ID} .cgt-ai-input input:focus { border-color: #2997FF; }
      #${PANEL_ID} .cgt-ai-input button {
        padding: 8px 14px; background: #2997FF; color: #fff;
        border: none; border-radius: 8px; cursor: pointer; font-weight: 600;
      }

      /* Gate cuando faltan findings */
      #${PANEL_ID} .cgt-ai-gate {
        text-align: center; padding: 24px 12px;
      }
      #${PANEL_ID} .cgt-ai-gate-icon { font-size: 32px; margin-bottom: 10px; opacity: 0.8; }
      #${PANEL_ID} .cgt-ai-gate h4 {
        font-size: 13px; color: #f5f5f7; margin: 0 0 10px;
        text-transform: none; letter-spacing: 0; font-weight: 600;
      }
      #${PANEL_ID} .cgt-ai-gate p {
        font-size: 11.5px; color: #a1a1a6; margin: 4px 0; line-height: 1.5;
      }
      #${PANEL_ID} .cgt-ai-gate-hint { color: #8e8e93; font-size: 11px; margin-top: 10px; }
      #${PANEL_ID} .cgt-ai-gate-btn {
        margin-top: 14px; padding: 8px 18px; background: rgba(48,209,88,0.15);
        color: #30D158; border: 0.5px solid rgba(48,209,88,0.35);
        border-radius: 8px; cursor: pointer; font-weight: 600; font-size: 12px;
        font-family: inherit;
      }
      #${PANEL_ID} .cgt-ai-gate-btn:hover { background: rgba(48,209,88,0.25); }

      /* Feedback por-sugerencia */
      #${PANEL_ID} .cgt-ai-fb-zone {
        display: flex; align-items: center; gap: 8px;
        margin-top: 16px; padding: 10px 12px;
        background: rgba(255,255,255,0.02);
        border: 0.5px dashed rgba(255,255,255,0.08);
        border-radius: 8px;
        font-size: 11px; color: #8e8e93;
      }
      #${PANEL_ID} .cgt-ai-fb-zone > span { flex: 1; }
      #${PANEL_ID} .cgt-ai-fb-zone button {
        padding: 4px 10px; font-size: 13px;
        background: rgba(255,255,255,0.04); border: 0.5px solid rgba(255,255,255,0.08);
        border-radius: 6px; cursor: pointer;
      }
      #${PANEL_ID} .cgt-ai-fb-zone button:hover { background: rgba(255,255,255,0.1); }
      #${PANEL_ID} .cgt-ai-thanks { color: #30D158; font-size: 11px; }

      @media (max-width: 1280px) {
        #${PANEL_ID} { width: 340px; right: 16px; }
      }
      @media (max-width: 640px) {
        #${PANEL_ID} {
          right: 12px; left: 12px; bottom: 12px; width: auto; max-height: 70vh;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

export const aiAssistantPanel = new AIAssistantPanel();
